import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import ClarityKit from "./pages/ClarityKit";
import Insights from "./pages/Insights";
import InsightPost from "./pages/InsightPost";
import Booking from "./pages/Booking";
import Contact from "./pages/Contact";
import CaseStudyIllustrative from "./pages/CaseStudyIllustrative";
import ClarityDiagnostic from "./pages/ClarityDiagnostic";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsAndConditions from "./pages/legal/TermsAndConditions";
import RefundPolicy from "./pages/legal/RefundPolicy";
import Disclaimer from "./pages/legal/Disclaimer";
import Admin from "./pages/Admin";

/**
 * Renders a static HTML page (served from /public) inside a full-viewport
 * iframe so the address bar keeps the clean URL (e.g. /clarity-kit) instead
 * of redirecting to /clarity-kit.html. The iframe is same-origin so the
 * HTML's own JavaScript runs unchanged.
 *
 * Importantly, the parent URL's query string and hash are forwarded into the
 * iframe `src` so that flows like /clarity-kit-preview?paid=true continue to
 * work — the inner page sees the same `window.location.search` it would have
 * seen if it were loaded as a top-level document.
 */
const StaticHtmlPage = ({ src, title }) => {
  // Forward parent query string + hash so the inner HTML sees them.
  // Read once on mount; this matches the behaviour of a top-level document
  // load (the inner page does not need to react to parent navigation).
  const search = typeof window !== "undefined" ? window.location.search : "";
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const iframeSrc = `${src}${search}${hash}`;

  useEffect(() => {
    if (title) document.title = title;
    // Hide html/body scrollbars: the iframe scrolls internally.
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
      bodyMargin: document.body.style.margin,
    };
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.margin = "0";
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.margin = prev.bodyMargin;
    };
  }, [title]);

  return (
    <iframe
      data-testid="static-html-frame"
      src={iframeSrc}
      title={title || "BuildOnce Studio"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        // 100dvh adapts to the Android Chrome address bar collapse and
        // the iOS Safari bottom toolbar, eliminating the black void below
        // the iframe when the dynamic viewport height changes.
        height: "100dvh",
        minHeight: "100vh",
        border: 0,
        margin: 0,
        padding: 0,
        display: "block",
        background: "#FDFCF8", // cream fallback, matches the inner page
      }}
    />
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Static HTML routes — render inside a fullscreen iframe so the
            address bar keeps the clean URL while the page's own scripts run.
            These bypass the site Layout (no header/footer) by design. */}
        <Route
          path="/clarity-kit-app"
          element={
            <StaticHtmlPage
              src="/clarity-kit-app.html"
              title="The BuildOnce Clarity Kit"
            />
          }
        />
        <Route
          path="/clarity-ai-demo"
          element={
            <StaticHtmlPage src="/clarity-ai-demo.html" title="Clarity Conversation · BuildOnce Studio" />
          }
        />
        {/* Free, publicly shareable Section 1 preview of the Clarity Kit.
            No paywall, no token — saves answers to the visitor's
            localStorage only. Replaces the previous alias that pointed at
            the sales page. */}
        {/* Retired 2026-08 (Deploy 2). The Kit preview competed with the
            Free Clarity Check for the same free-entry-point intent, so it
            is now redirected. On production the static host serves a bare
            meta-refresh + canonical stub at /clarity-kit-preview/index.html
            (see scripts/inject-per-route-meta.mjs `redirectTo`). This
            client-side <Navigate> is a defence-in-depth fallback that
            handles the dev server + any client-side link that lands here. */}
        <Route
          path="/clarity-kit-preview"
          element={<Navigate to="/clarity-diagnostic" replace />}
        />

        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/clarity-kit" element={<ClarityKit />} />
          {/* Back-compat alias: /clarity-kit-react still resolves to the
              full ClarityKit sales page. The previous /clarity-kit-preview
              alias has been re-routed (above) to the new Section 1 preview. */}
          <Route path="/clarity-kit-react" element={<ClarityKit />} />
          <Route path="/clarity-diagnostic" element={<ClarityDiagnostic />} />
          {/* Alias: /diagnostic was linked from external comms before the
              page was renamed to /clarity-diagnostic. Without this alias the
              route falls through React Router and paints a blank page. */}
          <Route path="/diagnostic" element={<ClarityDiagnostic />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightPost />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/contact" element={<Contact />} />
          <Route
            path="/case-studies/illustrative-clarity-sprint"
            element={<CaseStudyIllustrative />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/admin" element={<Admin />} />
          {/* Catch-all: any unknown route renders Home rather than a blank
              page. Prevents the historical /diagnostic blank-page issue
              from recurring with any other typo'd URL. */}
          <Route path="*" element={<Home />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
